/**
 * Controlled calendar-commitment metadata editor. Loads custom fields, derives the P/RJ mode, and
 * saves profile, attributes, the allow-conflict permission (for one event or its whole recurring
 * series), and protected-hold duration through the calendar API. Calls onSave with a normalized
 * event after success; event/open/onClose remain controlled by the parent.
 *
 * @module apps/web/src/components/calendar/CalendarCommitmentEditor
 */

import { useEffect, useState } from 'react';
import type { CalendarEventWire, AttributeDef, TaskStateProfile } from '@fluxability/types';
import { deriveTaskStateMode } from '@fluxability/types';
import type { CalendarEvent } from '@/stores/scheduleStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { formatTime } from '@/lib/utils';
import { EnumAttributeInput } from '@/components/attributes/EnumAttributeInput';
import { Layers, Loader2 } from 'lucide-react';
import { AfterFirstPaint } from '@/hooks/afterFirstPaint';

/** Initial neutral state-impact draft with null scores, preserving unknown impact rather than inventing zero-valued observations. */
const emptyProfile: TaskStateProfile = {
  mode: 'neutral',
  required_productivity_energy: null,
  required_rejuvenation_energy: null,
  productivity_return: null,
  rejuvenation_return: null,
  resistance: null,
};

/** Which events an allow-conflict change applies to, matching the API's `allow_conflict_scope`. */
type ConflictScope = 'occurrence' | 'series';

/** Selected commitment and controlled editor lifecycle, with a callback for an accepted saved event. */
interface CalendarCommitmentEditorProps {
  /** Selected timed commitment or protected hold. */
  event: CalendarEvent | null;
  /** Controlled editor visibility. */
  open: boolean;
  /** Request that the parent close the editor. */
  onClose: () => void;
  /** Receive the successfully saved and normalized calendar event. */
  onSave: (event: CalendarEvent) => void;
}

/**
 * Controlled calendar-commitment metadata editor. Loads custom fields, derives the P/RJ mode, and
 * saves profile, attributes, and protected-hold duration through the calendar API. Calls onSave
 * with a normalized event after success; event/open/onClose remain controlled by the parent.
 */
export function CalendarCommitmentEditor({
  event,
  open,
  onClose,
  onSave,
}: CalendarCommitmentEditorProps) {
  const [profile, setProfile] = useState<TaskStateProfile>(emptyProfile);
  const [customAttributes, setCustomAttributes] = useState<Record<string, unknown>>({});
  const [availableAttributes, setAvailableAttributes] = useState<AttributeDef[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [protectedDurationMinutes, setProtectedDurationMinutes] = useState(0);
  const [allowConflict, setAllowConflict] = useState(false);
  const [conflictScope, setConflictScope] = useState<ConflictScope>('occurrence');
  const initialScope: ConflictScope = event?.seriesAllowConflict ? 'series' : 'occurrence';

  useEffect(() => {
    if (!open) return;
    setProfile({ ...emptyProfile, ...(event?.stateProfile || {}) });
    setCustomAttributes(event?.customAttributes || {});
    setAllowConflict(Boolean(event?.allowConflict));
    setConflictScope(event?.seriesAllowConflict ? 'series' : 'occurrence');
    if (event?.start && event?.end) {
      setProtectedDurationMinutes(
        Math.max(
          5,
          Math.round((new Date(event.end).getTime() - new Date(event.start).getTime()) / 60000)
        )
      );
    }
    void loadAttributes();
  }, [event, open]);

  async function loadAttributes() {
    const response = await api.user.getAttributes();
    if (response.success && Array.isArray(response.data)) {
      setAvailableAttributes(response.data);
    }
  }

  function updateProfile(key: StateScoreKey, value: string) {
    setProfile(current => {
      const nextProfile: TaskStateProfile = {
        ...current,
        [key]: value === '' ? null : Math.max(0, Math.min(10, Number(value))),
      };
      return {
        ...nextProfile,
        mode: deriveTaskStateMode(nextProfile),
      };
    });
  }

  function updateAttribute(attrId: string, value: unknown) {
    setCustomAttributes(current => ({
      ...current,
      [attrId]: value,
    }));
  }

  function handleAttributeDefinitionUpdated(updatedAttr: AttributeDef) {
    setAvailableAttributes(current =>
      current.map(attr => (attr.attr_id === updatedAttr.attr_id ? updatedAttr : attr))
    );
  }

  async function handleSave() {
    if (!event) return;
    setIsSaving(true);
    try {
      const conflictChanged =
        !event.isFlexHold &&
        (allowConflict !== Boolean(event.allowConflict) ||
          (event.isRecurring && conflictScope !== initialScope));
      const response = await api.calendar.updateEvent(event.id, {
        state_profile: profile,
        custom_attributes: customAttributes,
        ...(conflictChanged
          ? {
              allow_conflict: allowConflict,
              allow_conflict_scope: event.isRecurring ? conflictScope : 'occurrence',
            }
          : {}),
        ...(event.isFlexHold && start
          ? {
              start: start.toISOString(),
              end: new Date(start.getTime() + protectedDurationMinutes * 60000).toISOString(),
            }
          : {}),
      });
      if (response.success && response.data) {
        onSave(normalizeCalendarEvent(response.data));
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  }

  const start = event ? new Date(event.start) : null;
  const end = event ? new Date(event.end) : null;
  const maxProtectedDuration =
    start && end ? Math.max(5, Math.round((end.getTime() - start.getTime()) / 60000)) : 0;

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle>Calendar Commitment</DialogTitle>
          <DialogDescription>
            {event?.title || 'Google Calendar event'}
            {start && end ? ` · ${formatTime(start)} - ${formatTime(end)}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-2">
          {event?.isFlexHold && (
            <div className="grid gap-3 border-b pb-4">
              <div>
                <h4 className="text-sm font-semibold">Protected Time</h4>
                <p className="text-muted-foreground text-xs">
                  Shorten this hold when you want to return part of the window to the scheduler.
                </p>
              </div>
              <div className="grid gap-2 sm:max-w-[220px]">
                <Label>Protected duration</Label>
                <Input
                  type="number"
                  min={5}
                  max={maxProtectedDuration || undefined}
                  step={5}
                  value={protectedDurationMinutes || ''}
                  onChange={inputEvent => {
                    const value = Number(inputEvent.target.value);
                    if (!Number.isFinite(value)) {
                      setProtectedDurationMinutes(5);
                      return;
                    }
                    setProtectedDurationMinutes(
                      Math.max(5, Math.min(maxProtectedDuration || value, Math.round(value)))
                    );
                  }}
                />
              </div>
            </div>
          )}

          {!event?.isFlexHold && (
            <AllowConflictSection
              allowConflict={allowConflict}
              onAllowConflictChange={setAllowConflict}
              isRecurring={Boolean(event?.isRecurring)}
              scope={conflictScope}
              onScopeChange={setConflictScope}
            />
          )}

          {/* Energy and attribute fields render after the dialog's first frame. */}
          <AfterFirstPaint>
            <div className="grid gap-4 border-b pb-4">
              <div>
                <h4 className="text-sm font-semibold">Actionable Energy</h4>
                <p className="text-muted-foreground text-xs">
                  These values stay in Fluxability and guide scheduling around this immovable
                  commitment.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label>Mode</Label>
                  <div className="bg-muted/40 flex h-10 items-center rounded-md border px-3 text-sm font-medium">
                    {formatMode(deriveTaskStateMode(profile))}
                  </div>
                </div>
                <ScoreField
                  label="Resistance"
                  value={profile.resistance}
                  onChange={value => updateProfile('resistance', value)}
                />
                <ScoreField
                  label="Required P"
                  value={profile.required_productivity_energy}
                  onChange={value => updateProfile('required_productivity_energy', value)}
                />
                <ScoreField
                  label="Required RJ"
                  value={profile.required_rejuvenation_energy}
                  onChange={value => updateProfile('required_rejuvenation_energy', value)}
                />
                <ScoreField
                  label="P Return"
                  value={profile.productivity_return}
                  onChange={value => updateProfile('productivity_return', value)}
                />
                <ScoreField
                  label="RJ Return"
                  value={profile.rejuvenation_return}
                  onChange={value => updateProfile('rejuvenation_return', value)}
                />
              </div>
            </div>

            <div className="grid gap-4">
              <div>
                <h4 className="text-sm font-semibold">Custom Attributes</h4>
                <p className="text-muted-foreground text-xs">
                  Apply your existing specificity and weight fields to this calendar commitment.
                </p>
              </div>
              {availableAttributes.length === 0 ? (
                <div className="bg-muted/30 text-muted-foreground rounded-md border p-3 text-sm">
                  Create custom fields from an Activity editor, then use them here.
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {availableAttributes.map(attr => (
                    <div key={attr.attr_id} className="grid gap-2">
                      <Label>{attr.name}</Label>
                      {attr.data_type === 'enum' ? (
                        <EnumAttributeInput
                          attr={attr}
                          value={customAttributes[attr.attr_id] || ''}
                          onChange={value => updateAttribute(attr.attr_id, value)}
                          onAttributeUpdated={handleAttributeDefinitionUpdated}
                        />
                      ) : ['Number', 'float', 'integer'].includes(attr.data_type) ? (
                        <Input
                          type="number"
                          value={String(customAttributes[attr.attr_id] ?? '')}
                          onChange={inputEvent =>
                            updateAttribute(
                              attr.attr_id,
                              inputEvent.target.value === '' ? '' : Number(inputEvent.target.value)
                            )
                          }
                          placeholder="Numeric value"
                        />
                      ) : (
                        <Input
                          value={String(customAttributes[attr.attr_id] ?? '')}
                          onChange={inputEvent =>
                            updateAttribute(attr.attr_id, inputEvent.target.value)
                          }
                          placeholder="Value"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </AfterFirstPaint>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !event}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Commitment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Inputs of the allow-conflict control; the editor owns the draft and saving. */
interface AllowConflictSectionProps {
  /** Draft permission; true lets the scheduler place activities over the event. */
  allowConflict: boolean;
  /** Report a toggled draft permission. */
  onAllowConflictChange: (value: boolean) => void;
  /** Whether the event belongs to a recurring series; only then is the scope choice shown. */
  isRecurring: boolean;
  /** Draft scope of the change; ignored for a single event. */
  scope: ConflictScope;
  /** Report a newly chosen scope. */
  onScopeChange: (scope: ConflictScope) => void;
}

/**
 * Switch that lets activities overlap a synced calendar event, with a "This event / All events in
 * the series" choice for recurring events. Holds no state of its own.
 */
function AllowConflictSection({
  allowConflict,
  onAllowConflictChange,
  isRecurring,
  scope,
  onScopeChange,
}: AllowConflictSectionProps) {
  return (
    <div className="grid gap-3 border-b pb-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 id="allow-conflict-label" className="flex items-center gap-1.5 text-sm font-semibold">
            <Layers className="h-4 w-4 text-blue-500" aria-hidden="true" />
            Allow conflicts
          </h4>
          <p id="allow-conflict-help" className="text-muted-foreground text-xs">
            Fluxability may schedule activities at the same time as this event.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={allowConflict}
          aria-labelledby="allow-conflict-label"
          aria-describedby="allow-conflict-help"
          onClick={() => onAllowConflictChange(!allowConflict)}
          className={
            'focus:ring-primary relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ' +
            (allowConflict ? 'bg-primary' : 'bg-muted')
          }
        >
          <span
            aria-hidden="true"
            className={
              'bg-background inline-block h-5 w-5 rounded-full shadow transition-transform ' +
              (allowConflict ? 'translate-x-5' : 'translate-x-0')
            }
          />
        </button>
      </div>
      {isRecurring && (
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-xs font-medium">This is a recurring event. Apply to</legend>
          <div className="flex flex-wrap gap-4 text-sm">
            {(
              [
                ['occurrence', 'This event'],
                ['series', 'All events in the series'],
              ] as const
            ).map(([value, label]) => (
              <label key={value} className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="allow-conflict-scope"
                  value={value}
                  checked={scope === value}
                  onChange={() => onScopeChange(value)}
                />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
      )}
    </div>
  );
}

/** Collect an optional state-impact score for a calendar commitment. The editor owns saving the score; this control only reports its numeric or empty draft value. */
function ScoreField({
  label,
  value,
  onChange,
}: {
  /** Visible caption describing this control or metric. */
  label: string;
  /** Optional numeric score; an absent value leaves the field empty. */
  value?: number | null;
  /** Report the edited draft value to the owner; this callback does not itself prove a saved server result. */
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <Input
        type="number"
        min={0}
        max={10}
        step={0.5}
        value={value ?? ''}
        onChange={event => onChange(event.target.value)}
        placeholder="0-10"
      />
    </div>
  );
}

/** Numeric state-impact keys editable on a commitment; the derived mode is excluded. */
type StateScoreKey = Exclude<keyof TaskStateProfile, 'mode'>;

/** Map the derived state mode to a display label, falling back to Neutral for the remaining case. */
function formatMode(mode: NonNullable<TaskStateProfile['mode']>): string {
  switch (mode) {
    case 'productive':
      return 'Productive';
    case 'rejuvenating':
      return 'Rejuvenating';
    case 'both':
      return 'Both';
    default:
      return 'Neutral';
  }
}

/** Adapt the saved commitment response (camelCase, from `PATCH /calendar/events/:id`) into the calendar-store shape, defaulting missing immovability to true and preserving its state profile, custom attributes and allow-conflict fields. Snake_case names are still accepted. */
function normalizeCalendarEvent(event: CalendarEventWire): CalendarEvent {
  return {
    id: event.id,
    title: event.title || event.summary || 'Calendar event',
    start: event.start || event.start_time || '',
    end: event.end || event.end_time || '',
    isImmovable: Boolean(event.isImmovable ?? event.is_immovable ?? true),
    isFlexHold: Boolean(event.isFlexHold ?? event.is_flex_hold),
    stateProfile: event.stateProfile || event.state_profile || null,
    customAttributes: event.customAttributes || event.custom_attributes || {},
    allowConflict: Boolean(event.allowConflict ?? event.allow_conflict),
    seriesAllowConflict: Boolean(event.seriesAllowConflict),
    isRecurring: Boolean(event.isRecurring),
  };
}
